import { getGSAPSettings } from '../utils/GSAP';
import { logWarn, showErrorOnElement } from './errors.js';

const checkMaskedText = document.querySelectorAll('[vdx-gsap-maskedtext]');

if (checkMaskedText.length > 0){    
    const firstEl = checkMaskedText[0];
    if (typeof gsap === 'undefined') {
        logWarn('GSAP is missing. Please turn on GSAP in Webflow.', { module: 'GSAPMaskedText' });
        showErrorOnElement(firstEl, 'GSAP is missing. Please turn on GSAP in Webflow.');
    }
    if (typeof SplitText === 'undefined') {
        logWarn('GSAP SplitText plugin is required. Enable it in Webflow settings.', { module: 'GSAPMaskedText' });
        showErrorOnElement(firstEl, 'GSAP SplitText plugin is required. Enable it in Webflow settings.');
    }
    if (typeof ScrollTrigger === 'undefined') {
        logWarn('ScrollTrigger not found. Activate it in Webflow for animations.', { module: 'GSAPMaskedText' });
        showErrorOnElement(firstEl, 'ScrollTrigger not found. Activate it in Webflow for animations.');
    }
}

if (checkMaskedText.length > 0 && typeof gsap !== 'undefined' && typeof SplitText !== 'undefined' && typeof ScrollTrigger !== 'undefined'){
    gsap.registerPlugin(SplitText, ScrollTrigger);
}   

export const maskedText = () => {
    if (checkMaskedText.length > 0 && typeof gsap !== 'undefined' && typeof SplitText !== 'undefined' && typeof ScrollTrigger !== 'undefined'){
    const maskedTextElements = gsap.utils.toArray('[vdx-gsap-maskedtext]');
    if (maskedTextElements.length === 0) {
        return;
    }

    // Create an Intersection Observer to trigger animations when elements are in view
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // Get the element and its stored animation data
                const el = entry.target;
                const preparedData = el._preparedAnimData;
                
                if (preparedData) {
                    triggerAnimation(preparedData);
                }
                
                // Unobserve after triggering the animation
                observer.unobserve(el);
            }
        });
    }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.2
    });

    // Function to prepare the element for animation
    const prepareElement = (el) => {
        // Get animation type from the attribute
        const animationType = el.getAttribute('vdx-gsap-maskedtext') || 'slide-up';
        
        // Check if animation should be disabled (value is 'false')
        if (animationType === 'false') {
            // Make element visible without animation
            gsap.set(el, { opacity: 1 });
            return;
        }
        
        const { duration, stagger, ease, disableBreakpoint } = getGSAPSettings(el);
        
        // Check if animations should be disabled based on breakpoint
        if (disableBreakpoint > 0 && window.innerWidth <= disableBreakpoint) {
            // Make all children visible without animation
            gsap.set(el.children, { opacity: 1 });
            return;
        }
        
        // Set initial opacity to avoid flicker
        gsap.set(el, { opacity: 1 });
        
        const splitInstance = new SplitText(el, {
            type: "chars,words,lines",
            mask: "lines"
        });
        
        // Set initial state for the lines based on animation type
        switch(animationType) {
            case 'slide-up':
                gsap.set(splitInstance.lines, { yPercent: 100, opacity: 0 });
                break;
                
            case 'slide-down':
                gsap.set(splitInstance.lines, { yPercent: -100, opacity: 0 });
                break;
                
            case 'bounce-up':
                gsap.set(splitInstance.lines, { yPercent: 100, opacity: 0 });
                break;
                
            case 'bounce-down':
                gsap.set(splitInstance.lines, { yPercent: -100, opacity: 0 });
                break;
                
            case 'rotate-up':
                gsap.set(splitInstance.lines, { yPercent: 100, rotation: 10, opacity: 0 });
                break;
                
            case 'rotate-down':
                gsap.set(splitInstance.lines, { yPercent: -100, rotation: -10, opacity: 0 });
                break;
                
            case 'random-up':
            case 'random-down':
                splitInstance.lines.forEach((line) => {
                    const randomX = (Math.random() - 0.5) * 50;
                    const randomRotation = (Math.random() - 0.5) * 20;
                    const yPercent = animationType === 'random-up' ? 100 : -100;
                    
                    gsap.set(line, { 
                        yPercent: yPercent,
                        xPercent: randomX,
                        rotation: randomRotation,
                        opacity: 0
                    });
                });
                break;
        }
        
        // Store the prepared data for animation
        return {
            el,
            splitInstance,
            animationType,
            duration,
            stagger,
            ease
        };
    };

    // Function to trigger the animation with prepared data
    const triggerAnimation = (data) => {
        const { splitInstance, animationType, duration, stagger, ease } = data;
        
        // Apply different animations based on the attribute value
        switch(animationType) {
            case 'slide-up':
                gsap.to(splitInstance.lines, {
                    duration: duration,
                    yPercent: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-down':
                gsap.to(splitInstance.lines, {
                    duration: duration,
                    yPercent: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'bounce-up':
                gsap.to(splitInstance.lines, {
                    duration: duration * 1.5,
                    yPercent: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: "bounce.out"
                });
                break;
                
            case 'bounce-down':
                gsap.to(splitInstance.lines, {
                    duration: duration * 1.5,
                    yPercent: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: "bounce.out"
                });
                break;
                
            case 'rotate-up':
                gsap.to(splitInstance.lines, {
                    duration: duration,
                    yPercent: 0,
                    rotation: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'rotate-down':
                gsap.to(splitInstance.lines, {
                    duration: duration,
                    yPercent: 0,
                    rotation: 0,
                    opacity: 1,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'random-up':
            case 'random-down':
                splitInstance.lines.forEach((line, index) => {
                    gsap.to(line, {
                        duration: duration,
                        yPercent: 0,
                        xPercent: 0,
                        rotation: 0,
                        opacity: 1,
                        delay: index * stagger,
                        ease: ease
                    });
                });
                break;
        }
    };

    // Process each element individually to prepare for animation
    maskedTextElements.forEach(el => {
        // Prepare the element and store the data
        const preparedData = prepareElement(el);
        el._preparedAnimData = preparedData;
        
        // Start observing the element
        observer.observe(el);
    });
    }
}